import React, { useState, useEffect, useRef } from 'react';
import type { AppUpdate } from '@/types';

const isSafeUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

// Object/data URLs are produced locally (blob preview, bundled mock asset) so they're safe to
// render. Bundled assets can also resolve to an app-relative path (e.g. "/assets/foo.png"),
// so resolve against the current origin before checking the protocol.
const isSafeImageUrl = (url: string): boolean => {
  if (url.startsWith('blob:') || url.startsWith('data:')) return true;
  try {
    const { protocol } = new URL(url, globalThis.location.origin);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

interface UpdateModalProps {
  update: AppUpdate;
  onClose: (dismissPermanently: boolean) => void;
}

const UpdateModal: React.FC<UpdateModalProps> = ({ update, onClose }) => {
  const [dismiss, setDismiss] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const dismissRef = useRef(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Track whether onClose has already been called to prevent double-firing
  const onCloseFiredRef = useRef(false);

  const showImage = !!update.imageUrl && !imageLoadFailed && isSafeImageUrl(update.imageUrl);

  // Keep ref in sync so handleClose always reads the latest checkbox value
  const handleDismissChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDismiss(e.target.checked);
    dismissRef.current = e.target.checked;
  };

  const handleClose = () => {
    if (onCloseFiredRef.current) return;
    onCloseFiredRef.current = true;
    if (dialogRef.current?.open) dialogRef.current.close();
    onClose(dismissRef.current);
  };

  // Reset error state if a new update/image is passed to an already-mounted modal
  useEffect(() => {
    setImageLoadFailed(false);
  }, [update.imageUrl]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    // Reset on each mount so StrictMode re-mount works correctly
    onCloseFiredRef.current = false;
    try {
      if (!dialog.open) dialog.showModal();
    } catch {
      // Ignore: dialog may already be open during StrictMode re-mount
    }
    return () => {
      // Cleanup: close the dialog silently (don't call onClose)
      if (dialog.open) dialog.close();
    };
  }, []);

  // Prevent default cancel (Escape) so we control the close sequence
  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    handleClose();
  };

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      aria-labelledby="update-modal-title"
      className="m-auto w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1a2535] p-0 backdrop:bg-black/60"
    >
      {/* Flex wrapper — lays panels side-by-side and anchors the absolute close button */}
      <div className="relative flex w-full max-h-[85vh]">
        {/* Left — image panel, only rendered when a usable image is available */}
        {showImage && (
          <div className="w-24 flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-[#0d1b2a] sm:w-64">
            <img
              src={update.imageUrl ?? undefined}
              alt={update.title}
              className="h-full w-full object-cover"
              onError={() => setImageLoadFailed(true)}
            />
          </div>
        )}

        {/* Right — content panel */}
        <div className="flex flex-1 flex-col p-8 dark:bg-[#1a2535] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-white dark:[&::-webkit-scrollbar-track]:bg-[#1a2535] [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-[#2e3f54] [&::-webkit-scrollbar-thumb]:rounded-full">
          {/* Close button */}
          <button
            type="button"
            className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center bg-[#0d1b2a] text-white hover:bg-[#0a1520]"
            onClick={handleClose}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="5"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          {/* Heading */}
          <h2
            id="update-modal-title"
            className="mb-5 text-3xl font-light text-gray-900 dark:text-white"
          >
            {update.title}
          </h2>

          {/* Body */}
          <div className="space-y-0 text-gray-700 dark:text-gray-300">
            {update.body.split('\n').map((line, i) => {
              const lineKey = `line-${i}-${line.slice(0, 20)}`;
              const isBullet = /^[•-]/.test(line);
              const text = isBullet ? line.slice(1).trimStart() : line;
              if (isBullet) {
                return (
                  <div key={lineKey} className="flex pl-4">
                    <span className="mr-2 flex-shrink-0">•</span>
                    <span>{text}</span>
                  </div>
                );
              }
              if (line.trim() === '') return <div key={lineKey} className="h-3" />;
              return <p key={lineKey}>{line}</p>;
            })}
          </div>

          {/* Demo link */}
          {update.demoUrl && isSafeUrl(update.demoUrl) && (
            <div className="mt-1">
              <a
                href={update.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                DEMO
              </a>
            </div>
          )}

          {/* Dismiss checkbox */}
          <label className="mt-auto pt-6 flex cursor-pointer items-center justify-end gap-3 text-sm text-gray-500 dark:text-gray-300">
            Don&apos;t show this update again
            <div className="relative h-5 w-5 flex-shrink-0">
              <input
                type="checkbox"
                checked={dismiss}
                onChange={handleDismissChange}
                className="h-5 w-5 appearance-none border-2 border-gray-500 bg-transparent checked:bg-gray-700 checked:border-gray-700 dark:border-white dark:checked:bg-white dark:checked:border-white cursor-pointer"
              />
              {dismiss && (
                <svg
                  className="pointer-events-none absolute inset-0 h-5 w-5 text-white dark:text-[#1a2535]"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                </svg>
              )}
            </div>
          </label>
        </div>
      </div>
    </dialog>
  );
};


export { UpdateModal };
